import { Link, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Instagram, MapPin } from "lucide-react";
import { NAV_LINKS, SITE, waLink } from "../../data/site";
import { SERVICES } from "../../data/services";
import { scrollToHash } from "../../lib/scroll";
import { track } from "../../lib/analytics";

export const Footer = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const go = (e, hash) => {
        e.preventDefault();
        if (location.pathname !== "/") navigate("/", { state: { scrollTo: hash } });
        else scrollToHash(hash);
    };

    return (
        <footer data-testid="site-footer" className="bg-ink border-t border-line text-white">
            <div className="container-x py-16 lg:py-20">
                <div className="grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3">
                            <img
                                src="/images/logo-emblem.png"
                                alt="Logo IMPRIMA AKI"
                                data-testid="footer-logo"
                                className="h-12 w-12 rounded-xl object-cover ring-1 ring-line"
                            />
                            <span className="font-display text-xl font-extrabold tracking-tight">
                                IMPRIMA<span className="text-brand"> AKI</span>
                            </span>
                        </div>
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-mist">{SITE.segments}</p>
                        <p className="mt-6 max-w-sm text-sm leading-relaxed text-mist">
                            Transformamos ideias, marcas e negócios em materiais que aparecem. Atendimento para empresas,
                            comércios e profissionais em Campinas e região.
                        </p>
                        <div className="mt-6 flex items-center gap-3">
                            <a
                                href={waLink()}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => track("whatsapp_click", { location: "footer" })}
                                data-testid="footer-whatsapp"
                                aria-label="WhatsApp"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-white/80 transition-colors hover:border-brand hover:text-brand"
                            >
                                <MessageCircle size={18} />
                            </a>
                            {SITE.instagram ? (
                                <a
                                    href={SITE.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-instagram"
                                    aria-label="Instagram"
                                    className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-white/80 transition-colors hover:border-brand hover:text-brand"
                                >
                                    <Instagram size={18} />
                                </a>
                            ) : (
                                <span
                                    data-testid="footer-instagram"
                                    title="Instagram — em breve"
                                    aria-label="Instagram em breve"
                                    className="flex h-11 w-11 items-center justify-center rounded-full border border-line/60 text-white/30"
                                >
                                    <Instagram size={18} />
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mist">Navegação</p>
                        <ul className="mt-5 space-y-3">
                            {NAV_LINKS.map((l) => (
                                <li key={l.hash}>
                                    <a
                                        href={`/${l.hash}`}
                                        onClick={(e) => go(e, l.hash)}
                                        data-testid={`footer-nav-${l.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                                        className="text-sm text-white/75 transition-colors hover:text-brand"
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            ))}
                            <li>
                                <Link to="/orcamento" data-testid="footer-nav-orcamento" className="text-sm text-white/75 transition-colors hover:text-brand">
                                    Formulário de orçamento
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="lg:col-span-4">
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mist">Serviços</p>
                        <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            {SERVICES.map((s) => (
                                <li key={s.slug}>
                                    <Link
                                        to={`/servicos/${s.slug}`}
                                        data-testid={`footer-service-${s.slug}`}
                                        className="text-sm text-white/75 transition-colors hover:text-brand"
                                    >
                                        {s.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-line pt-8">
                    <p className="flex items-center gap-2 text-sm text-mist">
                        <MapPin size={14} className="text-brand" />
                        {SITE.city} — WhatsApp {SITE.phoneDisplay}
                    </p>
                    <p className="text-xs text-mist/70">
                        © {new Date().getFullYear()} IMPRIMA AKI. Todos os direitos reservados.
                    </p>
                </div>
            </div>
        </footer>
    );
};
